import React, { useState } from 'react';
import { toast } from "sonner";
import { scrollToTop } from '../lib/utils';
import { emailService } from '../Api/EmailService';
import { Button } from '../components/ui/button';
import SecurityQuestion, { useSecurityQuestion } from '../components/SecurityQuestion';
import { hasText, stripLeadingSpaces, trimFormData, emailError, errorClass, fieldA11y, requiredName, requiredCompany, optionalPhoneError, validateDescriptionField, escapeFormData } from '../lib/formValidation';
import BotTrap, { useBotTrap } from '../components/BotTrap';
import FieldError from '../components/FieldError';
import { PERSONAL_DOMAINS } from '../data/mockData';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { CheckCircle, Clock, Users, Zap } from 'lucide-react';

// Shared field styling: gray field background, same blue as the card border on focus
const FIELD_CLASS =
  "bg-slate-800 border-slate-700 text-white placeholder:text-slate-500 " +
  "data-[placeholder]:text-slate-400 " +
  "focus:border-blue-500 focus:ring-1 focus:ring-blue-500 " +
  "focus-visible:border-blue-500 focus-visible:ring-1 focus-visible:ring-blue-500 " +
  // stop Chrome autofill from painting the field light blue
  "[&:-webkit-autofill]:[-webkit-text-fill-color:#fff] " +
  "[&:-webkit-autofill]:shadow-[inset_0_0_0_1000px_#1e293b] " +
  "[&:-webkit-autofill:focus]:shadow-[inset_0_0_0_1000px_#1e293b]";

// Dropdown list: same gray as the fields
const DROPDOWN_CLASS =
  "bg-slate-800 border-slate-700 text-white " +
  "[&_[role=option]]:text-white " +
  "[&_[role=option][data-highlighted]]:bg-slate-700 [&_[role=option][data-highlighted]]:text-white " +
  "[&_[role=option]:focus]:bg-slate-700 [&_[role=option]:focus]:text-white";

// Message for each required field (null = valid)
const validators = {
  firstName: (v) => requiredName(v, 'First name', 25),
  lastName: (v) => requiredName(v, 'Last name', 25),
  email: (v) => emailError(v, { workOnly: true, personalDomains: PERSONAL_DOMAINS }),
  company: (v) => requiredCompany(v, 'Company name', 50),
  jobTitle: (v) => requiredCompany(v, 'Job title', 50),
  phone: (v) => optionalPhoneError(v),
  challenges: (v) => validateDescriptionField(v, 2500),
};

const DemoPage = () => {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    company: '',
    jobTitle: '',
    phone: '',
    companySize: '',
    industry: '',
    currentCRM: '',
    challenges: '',
    demoType: ''
  });

  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  // Show a field's message only after the user has visited it
  const [fieldTouched, setFieldTouched] = useState({});
  // A field shows its message once the user has visited it or typed something,
  // so they always know why the button is disabled
  const getError = (field) => {
    const show = fieldTouched[field] || hasText(formData[field]);
    return show ? validators[field](formData[field]) : null;
  };
  const captcha = useSecurityQuestion();
  const trap = useBotTrap();

  // Button stays disabled until every required field has real text
  // (spaces only do not count) and the security question is answered
  const canSubmit =
    Object.keys(validators).every((field) => !validators[field](formData[field])) &&
    captcha.isCorrect;

  const createEmailPayload = (rawData) => {
    // everything typed by the visitor is escaped before it goes into the HTML email
    const data = escapeFormData(rawData);
    const { firstName, lastName, company } = data;

    const subject = `New Demo Request from ${rawData.firstName} ${rawData.lastName}${rawData.company ? ` at ${rawData.company}` : ''}`;

    const template = `<!DOCTYPE html PUBLIC "-//W3C//DTD HTML 4.01 Transitional//EN">
<html>
  <head>
    <meta content="text/html;charset=UTF-8" http-equiv="Content-Type">
    <link href="https://fonts.googleapis.com/css2?family=Noto+Sans:ital,wght@0,100..900;1,100..900&display=swap" rel="stylesheet">
    <style>
      body {
        font-family: 'Noto Sans', sans-serif;
        color: #000000;
      }
      h2 {
        font-family: 'Noto Sans', sans-serif;
      }
      p {
        font-family: 'Noto Sans', sans-serif;
        margin: 4px 0;
      }
    </style>
  </head>
  <body>
    <div>
      <h2>New Demo Request</h2>
      <p><strong>Name:</strong> ${firstName} ${lastName}</p>
      ${company ? `<p><strong>Company:</strong> ${company}</p>` : ''}
      <p><strong>Email:</strong> ${data.email}</p>
      <p><strong>Job Title:</strong> ${data.jobTitle}</p>
      <p><strong>Phone:</strong> ${data.phone}</p>
      <p><strong>Company Size:</strong> ${data.companySize}</p>
      <p><strong>Industry:</strong> ${data.industry}</p>
      <p><strong>Current CRM:</strong> ${data.currentCRM}</p>
      <p><strong>Challenges:</strong> ${data.challenges}</p>
      <p><strong>Preferred Demo Type:</strong> ${data.demoType}</p>
    </div>
  </body>
</html>`;
    return { subject, template };
  };
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!canSubmit || isSubmitting) return;
    if (trap.looksLikeBot()) {
      // pretend it worked, send nothing
      setIsSubmitted(true);
      return;
    }
    setIsSubmitted(false)
    setIsSubmitting(true)
    const payload = createEmailPayload(trimFormData(formData));
    const promise = emailService.sendEmail(payload); // this returns a promise

    // Show toast for this promise
    toast.promise(promise, {
      loading: "Sending your demo request...",
      success: "Demo request sent successfully!",
      error: "Failed to send demo request. Try again later.",
    });

    // Handle the result of the promise
    try {
      await promise;
      captcha.reset();
      setIsSubmitted(true);
      scrollToTop()
    }
    catch (error) {
      captcha.reset();
      setIsSubmitted(false)
      setIsSubmitting(false)
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      // no leading spaces, so a field can never be "only spaces"
      [field]: stripLeadingSpaces(value)
    }));
  };

  // Trim trailing spaces when the user leaves any text field of the form
  const handleFormBlur = (e) => {
    const { id, value, tagName } = e.target;
    if (id in validators) setFieldTouched(prev => ({ ...prev, [id]: true }));
    if ((tagName === 'INPUT' || tagName === 'TEXTAREA') && id in formData && typeof value === 'string') {
      const trimmed = value.trim();
      if (trimmed !== value) handleChange(id, trimmed);
    }
  };

  if (isSubmitted) {
    return (
      <div className="min-h-screen section-padding">
        <div className="container">
          <div className="max-w-2xl mx-auto text-center">
            <div className="w-20 h-20 rounded-full bg-green-400/10 flex items-center justify-center mx-auto mb-8">
              <CheckCircle className="h-10 w-10 text-green-400" />
            </div>
            <h1 className="display-md text-white mb-6">Demo Request Received!</h1>
            <p className="body-lg mb-8">
              Thank you for your interest in GrowSquare. Our sales team will contact you within 24 hours
              to schedule your personalized demo.
            </p>
            <div className="bg-slate-800 rounded-lg p-6 mb-8">
              <h3 className="h3 text-white mb-4">What happens next?</h3>
              <div className="space-y-4 text-left">
                <div className="flex items-start space-x-3">
                  <Clock className="h-5 w-5 text-green-400 mt-1" />
                  <div>
                    <p className="body-md font-medium text-white">Within 24 hours</p>
                    <p className="body-sm text-slate-400">Our sales expert will call you to understand your needs</p>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <Users className="h-5 w-5 text-green-400 mt-1" />
                  <div>
                    <p className="body-md font-medium text-white">Personalized Demo</p>
                    <p className="body-sm text-slate-400">We'll customize the demo based on your industry and use case</p>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <Zap className="h-5 w-5 text-green-400 mt-1" />
                  <div>
                    <p className="body-md font-medium text-white">Live Q&A Session</p>
                    <p className="body-sm text-slate-400">Get all your questions answered by our product experts</p>
                  </div>
                </div>
              </div>
            </div>
            <Button className="btn-secondary"
              onClick={() => {
                setIsSubmitted(false);
                setFieldTouched({});
                setFormData({
                  firstName: '',
                  lastName: '',
                  email: '',
                  company: '',
                  jobTitle: '',
                  phone: '',
                  companySize: '',
                  industry: '',
                  currentCRM: '',
                  challenges: '',
                  demoType: ''
                });
              }}>
              Request Another Demo
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen section-padding">
      <div className="container">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <h1 className="display-md text-white mb-6">Request Your Free Demo</h1>
            <p className="body-lg max-w-2xl mx-auto">
              See GrowSquare in action with a personalized demo tailored to your business needs.
              Discover how our AI-powered platform can transform your sales process.
            </p>
          </div>

          <div className="grid lg:grid-cols-3 gap-8">
            {/* Demo Form */}
            <div className="lg:col-span-2">
              <Card className="feature-card">
                <CardHeader>
                  <CardTitle className="h2 text-white">Demo Information</CardTitle>
                  <CardDescription className="body-md">
                    Fill out the form below and we'll contact you within 24 hours to schedule your demo.
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <form onSubmit={handleSubmit} onBlur={handleFormBlur} className="space-y-6">
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="firstName" className="text-white">First Name *</Label>
                        <Input
                          id="firstName"
                          autoComplete="given-name"
                          value={formData.firstName}
                          onChange={(e) => handleChange('firstName', e.target.value)}
                          required
                          {...fieldA11y('firstName', getError('firstName'))}
                          className={`${FIELD_CLASS} ${errorClass(getError('firstName'))}`}
                        />
                        <FieldError id="firstName-error" message={getError('firstName')} />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="lastName" className="text-white">Last Name *</Label>
                        <Input
                          id="lastName"
                          autoComplete="family-name"
                          value={formData.lastName}
                          onChange={(e) => handleChange('lastName', e.target.value)}
                          required
                          {...fieldA11y('lastName', getError('lastName'))}
                          className={`${FIELD_CLASS} ${errorClass(getError('lastName'))}`}
                        />
                        <FieldError id="lastName-error" message={getError('lastName')} />
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="email" className="text-white">Business Email *</Label>
                      <Input
                        id="email"
                        autoComplete="email"
                        type="email"
                        value={formData.email}
                        onChange={(e) => handleChange('email', e.target.value)}
                        required
                        {...fieldA11y('email', getError('email'))}
                        className={`${FIELD_CLASS} ${errorClass(getError('email'))}`}
                      />
                        <FieldError id="email-error" message={getError('email')} />
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="company" className="text-white">Company Name *</Label>
                        <Input
                          id="company"
                          autoComplete="organization"
                          value={formData.company}
                          onChange={(e) => handleChange('company', e.target.value)}
                          required
                          {...fieldA11y('company', getError('company'))}
                          className={`${FIELD_CLASS} ${errorClass(getError('company'))}`}
                        />
                        <FieldError id="company-error" message={getError('company')} />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="jobTitle" className="text-white">Job Title *</Label>
                        <Input
                          id="jobTitle"
                          autoComplete="organization-title"
                          value={formData.jobTitle}
                          onChange={(e) => handleChange('jobTitle', e.target.value)}
                          required
                          {...fieldA11y('jobTitle', getError('jobTitle'))}
                          className={`${FIELD_CLASS} ${errorClass(getError('jobTitle'))}`}
                        />
                        <FieldError id="jobTitle-error" message={getError('jobTitle')} />
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="phone" className="text-white">Phone Number</Label>
                      <Input
                        id="phone"
                        autoComplete="tel"
                        type="tel"
                        value={formData.phone}
                        onChange={(e) => handleChange('phone', e.target.value)}
                        {...fieldA11y('phone', getError('phone'))}
                        className={`${FIELD_CLASS} ${errorClass(getError('phone'))}`}
                      />
                      <FieldError id="phone-error" message={getError('phone')} />
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="companySize" className="text-white">Company Size</Label>
                        <Select value={formData.companySize} onValueChange={(value) => handleChange('companySize', value)}>
                          <SelectTrigger id="companySize" className={FIELD_CLASS}>
                            <SelectValue placeholder="Select company size" />
                          </SelectTrigger>
                          <SelectContent className={DROPDOWN_CLASS}>
                            <SelectItem value="1-10">1-10 employees</SelectItem>
                            <SelectItem value="11-50">11-50 employees</SelectItem>
                            <SelectItem value="51-200">51-200 employees</SelectItem>
                            <SelectItem value="201-1000">201-1000 employees</SelectItem>
                            <SelectItem value="1000+">1000+ employees</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="industry" className="text-white">Industry</Label>
                        <Select value={formData.industry} onValueChange={(value) => handleChange('industry', value)}>
                          <SelectTrigger id="industry" className={FIELD_CLASS}>
                            <SelectValue placeholder="Select industry" />
                          </SelectTrigger>
                          <SelectContent className={DROPDOWN_CLASS}>
                            <SelectItem value="technology">Technology & SaaS</SelectItem>
                            <SelectItem value="financial">Financial Services</SelectItem>
                            <SelectItem value="healthcare">Healthcare</SelectItem>
                            <SelectItem value="manufacturing">Manufacturing</SelectItem>
                            <SelectItem value="realestate">Real Estate</SelectItem>
                            <SelectItem value="professional">Professional Services</SelectItem>
                            <SelectItem value="other">Other</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="currentCRM" className="text-white">Current CRM Solution</Label>
                      <Select value={formData.currentCRM} onValueChange={(value) => handleChange('currentCRM', value)}>
                        <SelectTrigger id="currentCRM" className={FIELD_CLASS}>
                          <SelectValue placeholder="Select current CRM" />
                        </SelectTrigger>
                        <SelectContent className={DROPDOWN_CLASS}>
                          <SelectItem value="none">No CRM currently</SelectItem>
                          <SelectItem value="salesforce">Salesforce</SelectItem>
                          <SelectItem value="hubspot">HubSpot</SelectItem>
                          <SelectItem value="pipedrive">Pipedrive</SelectItem>
                          <SelectItem value="zoho">Zoho CRM</SelectItem>
                          <SelectItem value="other">Other</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="challenges" className="text-white">Current Challenges</Label>
                      <Textarea
                        id="challenges"
                        value={formData.challenges}
                        onChange={(e) => handleChange('challenges', e.target.value)}
                        placeholder="Tell us about your current sales challenges and what you're looking to improve..."
                        {...fieldA11y('challenges', getError('challenges'))}
                        className={`${FIELD_CLASS} ${errorClass(getError('challenges'))} min-h-[100px]`}
                      />
                        <FieldError id="challenges-error" message={getError('challenges')} />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="demoType" className="text-white">Demo Type Preference</Label>
                      <Select value={formData.demoType} onValueChange={(value) => handleChange('demoType', value)}>
                        <SelectTrigger id="demoType" className={FIELD_CLASS}>
                          <SelectValue placeholder="Select demo type" />
                        </SelectTrigger>
                        <SelectContent className={DROPDOWN_CLASS}>
                          <SelectItem value="general">General Product Overview</SelectItem>
                          <SelectItem value="ai">AI Features Focus</SelectItem>
                          <SelectItem value="automation">Automation Capabilities</SelectItem>
                          <SelectItem value="integration">Integration Options</SelectItem>
                          <SelectItem value="custom">Custom Use Case</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <BotTrap trap={trap} />
                    <SecurityQuestion captcha={captcha} fieldClass={FIELD_CLASS} />
                    <Button type="submit" className="btn-primary w-full glow-effect" disabled={isSubmitting || !canSubmit}>
                      {isSubmitting ? "Sending..." : "Request Demo"}
                    </Button>
                    {/* <Button type="submit" className="btn-primary w-full glow-effect">
                      Request Demo
                    </Button> */}
                  </form>
                </CardContent>
              </Card>
            </div>

            {/* Demo Benefits */}
            <div className="space-y-6">
              <Card className="feature-card">
                <CardHeader className="flex flex-col space-y-1.5 newP-1">
                  <CardTitle className="h3 text-white">What You'll See</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex items-start space-x-3">
                    <CheckCircle className="h-5 w-5 text-green-400 mt-1" />
                    <div>
                      <p className="body-md font-medium text-white">AI Lead Intelligence</p>
                      <p className="body-sm text-slate-400">See how AI identifies and engages prospects</p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-3">
                    <CheckCircle className="h-5 w-5 text-green-400 mt-1" />
                    <div>
                      <p className="body-md font-medium text-white">Automation Rules</p>
                      <p className="body-sm text-slate-400">Streamline workflows with smart automation</p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-3">
                    <CheckCircle className="h-5 w-5 text-green-400 mt-1" />
                    <div>
                      <p className="body-md font-medium text-white">Predictive Forecasting</p>
                      <p className="body-sm text-slate-400">Accurate revenue predictions and insights</p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-3">
                    <CheckCircle className="h-5 w-5 text-green-400 mt-1" />
                    <div>
                      <p className="body-md font-medium text-white">Integration Capabilities</p>
                      <p className="body-sm text-slate-400">Connect with your existing tools</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="feature-card">
                <CardHeader className="flex flex-col space-y-1.5 newP-1">
                  <CardTitle className="h3 text-white">Demo Promise</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="body-md mb-4">
                    Our 30-minute demo is completely tailored to your business needs. No generic presentations.
                  </p>
                  <div className="space-y-2">
                    {/* <p className="body-sm text-green-400">✓ No sales pressure</p> */}
                    <p className="body-sm text-green-400">✓ Industry-specific examples</p>
                    <p className="body-sm text-green-400">✓ Live Q&A session</p>
                    <p className="body-sm text-green-400">✓ ROI calculation</p>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
export default DemoPage;